/**
 * Scenario Runner page (Day 4).
 *
 * Layout:
 *   - Top bar: scenario picker (dropdown) + "Run" button + summary line
 *     («16 of 18 passed, 0 failed, 2 skipped — 4.2s»).
 *   - Body: vertical step list (one row per step) с цветной полоской
 *     слева (queued=gray / running=blue pulse / passed=green / failed=red
 *     / skipped=amber), inline `failure_reason`, и expandable JSON body
 *     для passed/failed steps.
 *
 * SSE consumption: POST /api/scenarios/:id/run возвращает `text/event-stream`.
 * Браузерный `EventSource` поддерживает только GET, поэтому мы используем
 * `fetch + ReadableStream + TextDecoder`, парсим frames вручную. Тот же
 * pattern, что во множестве production-приложений.
 */
import { useEffect, useRef, useState } from 'react';

import { Card, CardContent } from '../components/ui/card';
import { fetchScenarios, scenarioRunUrl } from '../lib/api';
import { cn } from '../lib/cn';
import type {
  ProxyResult,
  ScenarioCompletedEvent,
  ScenarioMeta,
  ScenarioStartedEvent,
  ScenarioStepEvent,
  StepStatus,
} from '../lib/types';

interface ParsedSseEvent {
  readonly eventName: string;
  readonly data: unknown;
}

/**
 * Parser для text/event-stream: takes accumulated buffer string, returns
 * array of complete frames and the remaining (unfinished) tail. Each frame
 * = lines separated by `\n`, terminated by blank line `\n\n`. Lines start
 * with `event:`, `data:`, or `:` (comment).
 */
function parseSseFrames(buffer: string): { frames: ParsedSseEvent[]; tail: string } {
  const out: ParsedSseEvent[] = [];
  let tail = buffer;
  while (true) {
    const split = tail.indexOf('\n\n');
    if (split === -1) break;
    const frame = tail.slice(0, split);
    tail = tail.slice(split + 2);
    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith(':')) continue;
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length === 0) continue;
    const dataStr = dataLines.join('\n');
    try {
      out.push({ eventName, data: JSON.parse(dataStr) });
    } catch {
      // Skip malformed frame.
    }
  }
  return { frames: out, tail };
}

export function ScenarioRunner() {
  const [catalog, setCatalog] = useState<readonly ScenarioMeta[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [steps, setSteps] = useState<readonly ScenarioStepEvent[]>([]);
  const [scenarioStart, setScenarioStart] = useState<ScenarioStartedEvent | null>(null);
  const [scenarioEnd, setScenarioEnd] = useState<ScenarioCompletedEvent | null>(null);
  const [running, setRunning] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const cat = await fetchScenarios();
        setCatalog(cat.scenarios);
        if (cat.scenarios.length > 0 && selectedId === '') {
          setSelectedId(cat.scenarios[0].id);
        }
      } catch (e) {
        setCatalogError((e as Error).message);
      }
    })();
    // selectedId is intentionally omitted from deps — set-once on cold load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const selectedMeta = catalog.find((s) => s.id === selectedId);

  async function runScenario() {
    if (selectedId === '' || running) return;
    setSteps([]);
    setScenarioStart(null);
    setScenarioEnd(null);
    setRunError(null);
    setRunning(true);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const res = await fetch(scenarioRunUrl(selectedId), {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        signal: ac.signal,
      });
      if (!res.ok) {
        setRunError(`HTTP ${res.status} from emulator backend`);
        setRunning(false);
        return;
      }
      const reader = res.body?.getReader();
      if (reader === undefined) {
        setRunError('emulator backend returned empty body (no SSE stream)');
        setRunning(false);
        return;
      }
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        if (chunk.done) {
          done = true;
          break;
        }
        buffer += decoder.decode(chunk.value, { stream: true });
        const { frames, tail } = parseSseFrames(buffer);
        buffer = tail;
        for (const frame of frames) {
          dispatchSseEvent(frame, {
            setScenarioStart,
            setScenarioEnd,
            updateStep,
          });
        }
      }
      // Drain any final frame in buffer.
      const finalChunk = decoder.decode();
      buffer += finalChunk;
      const { frames } = parseSseFrames(buffer + '\n\n');
      for (const frame of frames) {
        dispatchSseEvent(frame, { setScenarioStart, setScenarioEnd, updateStep });
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        setRunError('aborted');
      } else {
        setRunError((e as Error).message);
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  }

  function updateStep(ev: ScenarioStepEvent) {
    setSteps((prev) => {
      const next = [...prev];
      const idx = next.findIndex((s) => s.step_index === ev.step_index);
      if (idx === -1) {
        next.push(ev);
      } else {
        next[idx] = ev;
      }
      next.sort((a, b) => a.step_index - b.step_index);
      return next;
    });
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold text-slate-900">Scenario Runner</h2>
        <p className="text-sm text-slate-500">
          Clickable port of <code>examples/op-client-typescript/src/full-flow.ts</code> — full
          16-step happy path + 5 error-injection scenarios. Webhook deliveries appear в Webhook
          Timeline tab параллельно с прогоном.
        </p>
      </header>

      {catalogError && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load scenario catalog: {catalogError}
        </div>
      )}

      <Card>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex-1 min-w-[300px]">
              <span className="block text-xs font-medium text-slate-600 mb-1">Scenario</span>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={running}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
              >
                {catalog.map((s) => (
                  <option key={s.id} value={s.id}>
                    [{s.group}] {s.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={runScenario}
              disabled={running || selectedId === ''}
              className={cn(
                'px-4 py-1.5 rounded text-sm font-medium transition-colors',
                running
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-brand text-white hover:bg-brand-dark',
              )}
            >
              {running ? 'Running…' : 'Run'}
            </button>
            {running && (
              <button
                type="button"
                onClick={() => abortRef.current?.abort()}
                className="px-3 py-1.5 rounded text-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Abort
              </button>
            )}
          </div>
          {selectedMeta && (
            <div className="mt-3 space-y-1">
              <p className="text-sm text-slate-700">{selectedMeta.summary}</p>
              {selectedMeta.notes && (
                <p className="text-xs italic text-amber-700">⚠️ {selectedMeta.notes}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {scenarioStart && (
        <Card>
          <CardContent>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <div>
                <span className="font-medium text-slate-900">{scenarioStart.scenario_name}</span>
                <span className="text-slate-400"> · started {scenarioStart.started_at}</span>
              </div>
              {scenarioEnd && (
                <div
                  className={cn(
                    'px-2 py-0.5 rounded text-xs font-medium',
                    scenarioEnd.status === 'passed'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-red-100 text-red-800',
                  )}
                >
                  {scenarioEnd.status === 'passed' ? '✓ PASSED' : '✗ FAILED'} ·{' '}
                  {scenarioEnd.passed_count} passed · {scenarioEnd.failed_count} failed ·{' '}
                  {scenarioEnd.skipped_count} skipped · {scenarioEnd.elapsed_ms} ms
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {runError && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Run error: {runError}
        </div>
      )}

      {steps.length === 0 && !running && !scenarioStart && (
        <div className="text-sm text-slate-500 px-4 py-8 text-center border border-dashed rounded">
          Pick a scenario and click <strong>Run</strong>. Each step streams progress here в реальном
          времени.
        </div>
      )}

      {steps.length > 0 && (
        <div className="space-y-2">
          {steps.map((step) => (
            <StepRow key={step.step_index} step={step} />
          ))}
        </div>
      )}
    </div>
  );
}

interface StepRowProps {
  readonly step: ScenarioStepEvent;
}

function StepRow({ step }: StepRowProps) {
  const [expanded, setExpanded] = useState(false);
  const statusBadge = statusBadgeFor(step.status);
  const stripe = stripeFor(step.status);
  const showExpand = step.result !== undefined;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-stretch">
        <div className={cn('w-1.5', stripe)} />
        <div className="flex-1 p-3 space-y-1">
          <div className="flex items-baseline justify-between gap-3">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs font-mono text-slate-400 shrink-0">
                #{String(step.step_index).padStart(2, '0')}
              </span>
              <span className="text-sm text-slate-800 truncate">{step.name}</span>
              <span
                className={cn(
                  'shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded',
                  groupBadge(step.group),
                )}
              >
                {step.group}
              </span>
            </div>
            <div className="flex items-baseline gap-2 shrink-0">
              {step.expects_error_code !== undefined && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">
                  expects {step.expects_error_code}
                </span>
              )}
              {step.elapsed_ms !== undefined && (
                <span className="text-xs text-slate-400">{step.elapsed_ms} ms</span>
              )}
              <span
                className={cn(
                  'text-xs font-medium px-2 py-0.5 rounded uppercase tracking-wide',
                  statusBadge,
                )}
              >
                {step.status}
              </span>
            </div>
          </div>
          {step.note && <p className="text-xs italic text-slate-500">📝 {step.note}</p>}
          {step.failure_reason && (
            <p className="text-xs text-red-700 font-mono">⚠ {step.failure_reason}</p>
          )}
          {showExpand && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-brand hover:underline"
            >
              {expanded ? '▾ Hide response' : '▸ Show response'}
            </button>
          )}
          {expanded && step.result !== undefined && (
            <div className="mt-2 border-t pt-2">
              <CompactProxyResultView result={step.result} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

interface CompactProxyResultViewProps {
  readonly result: ProxyResult;
}

function CompactProxyResultView({ result }: CompactProxyResultViewProps) {
  const meta =
    result.proxy_status === 'ok'
      ? `${result.gateway_method} ${result.gateway_url} → HTTP ${result.gateway_status_code} · ${result.elapsed_ms}ms${result.idempotent_replayed ? ' · replayed' : ''}${
          result.idempotency_key !== undefined
            ? ` · idem=${result.idempotency_key.slice(0, 8)}…`
            : ''
        }`
      : `${result.gateway_method} ${result.gateway_url} → ${result.error_kind}: ${result.error_message} · ${result.elapsed_ms}ms`;
  const body = result.proxy_status === 'ok' ? JSON.stringify(result.gateway_body, null, 2) : null;
  return (
    <div className="text-xs space-y-1">
      <div className="font-mono text-slate-500 break-all">{meta}</div>
      {body !== null && (
        <pre className="font-mono text-[11px] bg-slate-50 rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-slate-800">
          {body}
        </pre>
      )}
    </div>
  );
}

function statusBadgeFor(s: StepStatus): string {
  switch (s) {
    case 'queued':
      return 'bg-slate-100 text-slate-600';
    case 'running':
      return 'bg-sky-100 text-sky-800 animate-pulse';
    case 'passed':
      return 'bg-emerald-100 text-emerald-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'skipped':
      return 'bg-amber-100 text-amber-800';
  }
}

function stripeFor(s: StepStatus): string {
  switch (s) {
    case 'queued':
      return 'bg-slate-200';
    case 'running':
      return 'bg-sky-400';
    case 'passed':
      return 'bg-emerald-500';
    case 'failed':
      return 'bg-red-500';
    case 'skipped':
      return 'bg-amber-400';
  }
}

function groupBadge(g: 'preflight' | 'business' | 'cleanup'): string {
  if (g === 'preflight') return 'bg-slate-100 text-slate-600';
  if (g === 'cleanup') return 'bg-slate-100 text-slate-600';
  return 'bg-indigo-50 text-indigo-700';
}

function dispatchSseEvent(
  ev: ParsedSseEvent,
  ctx: {
    setScenarioStart: (e: ScenarioStartedEvent) => void;
    setScenarioEnd: (e: ScenarioCompletedEvent) => void;
    updateStep: (s: ScenarioStepEvent) => void;
  },
): void {
  switch (ev.eventName) {
    case 'scenario.started':
      ctx.setScenarioStart(ev.data as ScenarioStartedEvent);
      break;
    case 'scenario.completed':
      ctx.setScenarioEnd(ev.data as ScenarioCompletedEvent);
      break;
    case 'step.started':
    case 'step.completed':
      ctx.updateStep(ev.data as ScenarioStepEvent);
      break;
    default:
      // ignore unknown event names — forward compat
      break;
  }
}
